import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { Checkbox, InlineNotification } from "@carbon/react";
import DateField from "../../../components/carbon/Form/DateField";
import SelectField from "../../../components/carbon/Form/SelectField";
import TextareaField from "../../../components/carbon/Form/TextareaField";
import TextField from "../../../components/carbon/Form/TextField";
import MultiEmailInput from "../../../components/UI/InputField/MultiEmailInput";
import OcDocumentAttachment from "./OcDocumentAttachment";
import { useNotification } from "../../../context";
import { useCoordinateOc } from "../../../hooks/useCoordinateOc";
import { isOcConflict } from "../../../hooks/useOcActions";
import {
  CoordinateOcRequest,
  PurchaseOrderResponse,
  SiteResponse,
} from "../../../types";
import "./coordinateOc.scss";

interface CoordinateFormFields {
  deliveryDate: string;
  deliveryTime: string;
  deliveryWindow: string;
  recipientEmails: string[];
  comment: string;
}

/**
 * An already-scheduled date wins (editing); otherwise the backend's lead-time suggestion, then the
 * requested date, then empty.
 */
const initialDeliveryDate = (oc: PurchaseOrderResponse): string =>
  oc.scheduledDate ??
  oc.suggestedDeliveryDate ??
  oc.requestedDeliveryDate ??
  "";

const toDefaults = (
  oc: PurchaseOrderResponse,
  site?: SiteResponse
): CoordinateFormFields => ({
  deliveryDate: initialDeliveryDate(oc),
  deliveryTime: oc.scheduledTime ?? "",
  deliveryWindow: oc.deliveryWindow ?? site?.deliveryWindows?.[0] ?? "",
  recipientEmails: (site?.defaultRecipients ?? [])
    .map((r) => r.email)
    .filter(Boolean),
  comment: "",
});

/**
 * Only what the user entered goes out; blank fields are omitted so the backend defaults them
 * (requested date, the site's recipients). Recipient names are carried over from the site's
 * default recipients when the email matches one.
 */
const toRequest = (
  values: CoordinateFormFields,
  acknowledged: string[],
  site?: SiteResponse
): CoordinateOcRequest => {
  const body: CoordinateOcRequest = {};
  if (values.deliveryDate) body.deliveryDate = values.deliveryDate;
  if (values.deliveryTime.trim())
    body.deliveryTime = values.deliveryTime.trim();
  if (values.deliveryWindow.trim())
    body.deliveryWindow = values.deliveryWindow.trim();
  if (values.recipientEmails.length > 0) {
    const names = new Map(
      (site?.defaultRecipients ?? []).map((r) => [r.email, r.name])
    );
    body.recipients = values.recipientEmails.map((email) => {
      const name = names.get(email);
      return name ? { name, email } : { email };
    });
  }
  if (values.comment.trim()) body.comment = values.comment.trim();
  if (acknowledged.length > 0) body.acknowledgedRequirements = acknowledged;
  return body;
};

/** Imperative handle so a host (modal footer, page button bar) can submit the form from outside it. */
export interface CoordinateOcFormHandle {
  submit: () => void;
}

interface CoordinateOcFormProps {
  oc: PurchaseOrderResponse;
  site?: SiteResponse;
  /** The mutation is owned by the host so it can drive its own footer's loading/disabled state. */
  coordinate: ReturnType<typeof useCoordinateOc>;
  /** Called after a successful coordinate (modal: close; page: back to the list / advance). */
  onSuccess: () => void;
}

/**
 * The delivery-coordination form for one OC: fields (date / time / window / recipients /
 * requirement checklist / comment) prefilled from the OC and its Site, plus the attachment viewer.
 * Layout-agnostic — it renders no modal or page chrome; the host wraps it and triggers {@link
 * CoordinateOcFormHandle.submit}. Mounted once the Site has settled, so the prefill is part of the
 * form's initial state.
 */
const CoordinateOcForm = forwardRef<
  CoordinateOcFormHandle,
  CoordinateOcFormProps
>(({ oc, site, coordinate, onSuccess }, ref) => {
  const { t } = useTranslation();
  const { openNotification } = useNotification();
  const defaults = useMemo(() => toDefaults(oc, site), [oc, site]);
  // The hint only applies while the suggestion is the value actually prefilled.
  const dateIsSuggested =
    !oc.scheduledDate && Boolean(oc.suggestedDeliveryDate);
  const [acknowledged, setAcknowledged] = useState<string[]>([]);
  const { control, register, setValue, getValues, handleSubmit, setError } =
    useForm<CoordinateFormFields>({ defaultValues: defaults });

  const windows = site?.deliveryWindows ?? [];
  const requirements = site?.deliveryRequirements ?? [];

  const onSubmit = (values: CoordinateFormFields) => {
    // The backend needs a time or a window (400 otherwise): fail fast, inline.
    if (!values.deliveryTime.trim() && !values.deliveryWindow.trim()) {
      setError("deliveryTime", {
        message: t("purchaseOrders.coordinate.timeOrWindowRequired"),
      });
      return;
    }
    if (!values.deliveryDate) {
      setError("deliveryDate", {
        message: t("purchaseOrders.coordinate.dateRequired"),
      });
      return;
    }
    coordinate.mutate(
      {
        id: oc.id,
        body: toRequest(
          { ...values, recipientEmails: getValues("recipientEmails") ?? [] },
          acknowledged,
          site
        ),
      },
      {
        onSuccess: () => {
          openNotification(t("purchaseOrders.coordinate.success"));
          onSuccess();
        },
        onError: (error) =>
          openNotification(
            t(
              isOcConflict(error)
                ? "purchaseOrders.coordinate.notAccepted"
                : "purchaseOrders.coordinate.error"
            )
          ),
      }
    );
  };

  useImperativeHandle(ref, () => ({ submit: () => handleSubmit(onSubmit)() }));

  const toggleRequirement = (title: string, checked: boolean) =>
    setAcknowledged((current) =>
      checked ? [...current, title] : current.filter((x) => x !== title)
    );

  return (
    <>
      <OcDocumentAttachment ocId={oc.id} ocCode={oc.code} />
      <form
        id="coordinate-oc-form"
        className="coordinate-oc__form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        {oc.status === "SENT" ? (
          <InlineNotification
            kind="info"
            lowContrast
            hideCloseButton
            title=""
            subtitle={t("purchaseOrders.coordinate.proposalHint")}
          />
        ) : null}
        <div className="coordinate-oc__grid">
          <DateField
            name="deliveryDate"
            label={t("purchaseOrders.coordinate.date")}
            control={control}
            helperText={
              dateIsSuggested
                ? t("purchaseOrders.coordinate.suggestedDateHint")
                : undefined
            }
          />
          <TextField
            name="deliveryTime"
            label={t("purchaseOrders.coordinate.time")}
            placeholder="HH:mm"
            control={control}
          />
          {windows.length > 0 ? (
            <SelectField
              name="deliveryWindow"
              label={t("purchaseOrders.coordinate.window")}
              control={control}
              placeholder={t("purchaseOrders.coordinate.windowPlaceholder")}
              options={windows.map((w) => ({ value: w, label: w }))}
            />
          ) : (
            <TextField
              name="deliveryWindow"
              label={t("purchaseOrders.coordinate.window")}
              control={control}
            />
          )}
        </div>

        <MultiEmailInput
          id="recipientEmails"
          name="recipientEmails"
          label={t("purchaseOrders.coordinate.recipients")}
          register={register}
          setValue={setValue}
          value={defaults.recipientEmails}
        />

        {requirements.length > 0 ? (
          <fieldset className="coordinate-oc__requirements">
            <legend className="cds--label">
              {t("purchaseOrders.coordinate.requirements")}
            </legend>
            {requirements.map((r, index) => (
              <Checkbox
                key={`${r.title}-${index}`}
                id={`coordinate-requirement-${index}`}
                labelText={r.detail ? `${r.title} - ${r.detail}` : r.title}
                checked={acknowledged.includes(r.title)}
                onChange={(_event, { checked }) =>
                  toggleRequirement(r.title, checked)
                }
              />
            ))}
          </fieldset>
        ) : null}

        <TextareaField
          name="comment"
          label={t("purchaseOrders.coordinate.comment")}
          control={control}
          rows={3}
        />
      </form>
    </>
  );
});

CoordinateOcForm.displayName = "CoordinateOcForm";

export default CoordinateOcForm;
